import PropTypes from 'prop-types';

// Styles
import classnames from 'classnames';
import styled from 'styled-components';

import { Close } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { PillButton as UIPillButton } from '@rcg/ui-components';

const Button = styled(UIPillButton)`
	&.blackFont:not(.selected) {
		color: ${({ palette }) => palette.common.black};
	}

	&.specialColor {
		border-color: ${({ palette }) => palette.special.main};
		background-color: ${({ palette }) => palette.special.main};
		&:hover {
			border-color: ${({ palette }) => palette.special.dark};
			background-color: ${({ palette }) => palette.special.dark};
		}
	}

	&.tall {
		height: 40px;
	}

	&.isToast {
		bottom: 40px;
		left: 50%;
		position: fixed;
		transform: translate(-50%, 0);
		white-space: nowrap;
		z-index: 1;
	}
`;

/**
 * Basic button used throughout the app.
 * Has a `selected` state for toggle functionality.
 *
 * @param {bool} selected
 * @param {func} onClick
 * @param {string} className
 * @param {string=''} id
 * @param {string} children Wraps label
 * @param {bool} showX show 'X' svg after children, used to close
 * @param {bool} tall Style prop
 * @param {('button'|'submit')} type Should be one of two strings
 * @param {(string|node)} as Used by Styled Components to change html tag type
 */

function PillButton({
	selected,
	onClick,
	onClose,
	className,
	children,
	showX = true,
	type = 'button',
	tall,
	id = '',
	blackFont,
	specialColor,
	isToast,
	dataTestId,
}) {
	const { palette } = useTheme();
	const classes = classnames({
		[className]: !!className,
		selected,
		tall,
		blackFont,
		specialColor,
		isToast,
	});

	return (
		<Button
			id={id}
			className={classes}
			selected={selected}
			endIcon={showX ? <Close /> : ''}
			onClick={onClick}
			onClose={onClose}
			type={type}
			specialColor={specialColor}
			palette={palette}
			data-testid={dataTestId}
		>
			{children}
		</Button>
	);
}

PillButton.propTypes = {
	selected: PropTypes.bool,
	onClick: PropTypes.func,
	onClose: PropTypes.func,
	className: PropTypes.string,
	id: PropTypes.string,
	children: PropTypes.string,
	showX: PropTypes.bool,
	type: PropTypes.oneOf(['button', 'submit']),
	tall: PropTypes.bool,
	blackFont: PropTypes.bool,
	specialColor: PropTypes.bool,
	isToast: PropTypes.bool,
	palette: PropTypes.object,
};

export default PillButton;
